// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/** The algorithm registry — the single source of truth for what Verisum can compute. */
import type { AlgoId, AlgoInfo } from './types';

export const ALGORITHMS: AlgoInfo[] = [
  {
    id: 'sha256',
    label: 'SHA-256',
    hexLength: 64,
    strong: true,
    note: 'The modern default. What most ISOs, releases and package indexes publish.',
  },
  {
    id: 'sha512',
    label: 'SHA-512',
    hexLength: 128,
    strong: true,
    note: 'Longer SHA-2 digest, common for large release artefacts.',
  },
  {
    id: 'sha1',
    label: 'SHA-1',
    hexLength: 40,
    strong: false,
    note: 'Legacy. Still used by Git object ids; broken for security use.',
  },
  {
    id: 'md5',
    label: 'MD5',
    hexLength: 32,
    strong: false,
    note: 'Fast, ubiquitous, cryptographically broken — fine only for corruption checks.',
  },
  {
    id: 'sha3-256',
    label: 'SHA3-256',
    hexLength: 64,
    strong: true,
    note: 'The Keccak-based SHA-3 standard, a structural alternative to SHA-2.',
  },
  {
    id: 'blake2b-256',
    label: 'BLAKE2b-256',
    hexLength: 64,
    strong: true,
    note: 'Fast, strong hash used across modern tooling (e.g. some Debian indexes).',
  },
  {
    id: 'blake3',
    label: 'BLAKE3',
    hexLength: 64,
    strong: true,
    note: 'Very fast modern tree hash, increasingly common for large files.',
  },
  {
    id: 'xxh64',
    label: 'xxHash-64',
    hexLength: 16,
    strong: false,
    note: 'Extremely fast non-cryptographic checksum for corruption detection only.',
  },
  {
    id: 'crc32',
    label: 'CRC-32',
    hexLength: 8,
    strong: false,
    note: 'Classic 32-bit checksum (ZIP, PNG). Corruption detection, not security.',
  },
];

export const ALGO_IDS: AlgoId[] = ALGORITHMS.map((a) => a.id);

const BY_ID = new Map<AlgoId, AlgoInfo>(ALGORITHMS.map((a) => [a.id, a]));

export function algoInfo(id: AlgoId): AlgoInfo {
  const info = BY_ID.get(id);
  if (!info) throw new Error(`Unknown algorithm: ${id}`);
  return info;
}

export function algoLabel(id: AlgoId): string {
  return algoInfo(id).label;
}

/** Algorithms whose hex digest is exactly `len` characters long. */
export function algosByHexLength(len: number): AlgoId[] {
  return ALGORITHMS.filter((a) => a.hexLength === len).map((a) => a.id);
}

/** Algorithms shown as quick toggles by default (the common verification set). */
export const DEFAULT_VISIBLE: AlgoId[] = ['sha256', 'sha512', 'sha1', 'md5'];

/** Algorithm enabled on first load. */
export const DEFAULT_SELECTED: AlgoId[] = ['sha256'];

/**
 * When several algorithms share a digest length, this is the order we prefer
 * when auto-enabling one to satisfy a pasted checksum (strong & common first).
 */
export const PREFERENCE_ORDER: AlgoId[] = [
  'sha256',
  'sha512',
  'sha1',
  'md5',
  'blake3',
  'blake2b-256',
  'sha3-256',
  'xxh64',
  'crc32',
];

/** Pick the single most-likely algorithm for a given digest length. */
export function preferredAlgoForLength(len: number): AlgoId | null {
  const candidates = new Set(algosByHexLength(len));
  for (const id of PREFERENCE_ORDER) if (candidates.has(id)) return id;
  return null;
}
